import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useBetaAccess } from "@/lib/shared/beta-access";
import { useTierCredits } from "@/lib/shared/tier-credits";

/**
 * Route guard for platform-funded AI features (mining, resume, quick import).
 *
 * Non-beta users are sent to /beta to join the waitlist — platform AI is
 * gated behind the beta program. Beta users with exhausted credits are sent
 * to the upgrade page instead.
 *
 * Fail-open: if the beta check itself errors (network flap), the user passes
 * through — the byok-proxy enforces beta access server-side, and bouncing a
 * granted beta user to the waitlist over a transient RPC failure is worse
 * than letting the server return the real denial.
 */
export function RequireLLMAccess({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { isBeta, checkFailed, loading: betaLoading } = useBetaAccess();
  const { loading: creditsLoading, creditsExhausted } = useTierCredits();

  if (betaLoading || creditsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!isBeta && !checkFailed) {
    // returnTo as a query param — same pattern LoginPage reads — so the beta
    // page can send granted users back where they were headed. (Router state
    // was unused; nothing read it.)
    return <Navigate to={`/beta?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  }

  if (creditsExhausted) {
    return <Navigate to="/settings/upgrade" replace />;
  }

  return <>{children}</>;
}
